import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { POSReceiptDialog } from './pos/POSReceiptDialog';
import { POSHoldSaleDialog, POSHeldSalesDialog, POSRecallConfirmDialog } from './pos/POSHoldDialogs';
import { POSOfflineBanners } from './pos/POSOfflineBanners';
import { POSHardwareBar } from './pos/POSHardwareBar';
import { POSCatalogToolbar } from './pos/POSCatalogToolbar';
import { POSProductGrid } from './pos/POSProductGrid';
import { POSCatalogHeading } from './pos/POSCatalogHeading';
import { loadCachedCatalog, loadOnlineCatalog } from '../lib/posCatalog';
import { isPOSAvailable, isPOSBatchAvailable } from '../lib/posAvailability';
import { useFrequentLimit } from './pos/useFrequentLimit';
import { offlineScopeKey } from '../lib/offlineScope';
import './pos/pos.css';
import { POSCartPanel } from './pos/POSCartPanel';
import { POSMobileView } from './pos/POSMobileView';
import { POSAISearchDialog } from './pos/POSAISearchDialog';
import { POSCheckoutDialog } from './pos/POSCheckoutDialog';
import { POSControlledSubstanceGate } from './pos/POSControlledSubstanceGate';
import {
  getCachedProducts,
  getCachedProduct,
  searchCachedProducts,
  searchCachedCustomers,
  getCachedRefillEligibility,
  addOfflineSale,
} from '../lib/offlineDB';
import { useOfflinePreparation } from './pos/useOfflinePreparation';
import { TillRecallBanner } from '../components/pos/TillRecallBanner';
import { OpeningFloatDialog } from '../components/shift/OpeningFloatDialog';
import {
  connectThermalPrinter, disconnectThermalPrinter, isThermalPrinterConnected,
  smartPrintReceipt, openCashDrawerSafe, startBarcodeListener,
  openCustomerDisplay, closeCustomerDisplay, isCustomerDisplayOpen, updateCustomerDisplay,
} from '../lib/hardwareService';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { useTillScanner } from '../scanner/useTillScanner';
import { TillScannerPanel } from '../scanner/TillScannerPanel';
import { OpenedPackDialog, printPackLabel } from '../scanner/OpenedPackDialog';
import { Button } from '../components/ui/button';

function POSWorkspace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const frequentLimit = useFrequentLimit();
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogPage, setCatalogPage] = useState(1);
  const catalogRequest = useRef(0);
  const [cart, setCart] = useState([]);
  const scannerCartRef = useRef(cart);
  scannerCartRef.current = cart;
  const [clientSaleId, setClientSaleId] = useState(() => crypto.randomUUID());
  const scanner = useTillScanner(clientSaleId);
  const [packTarget, setPackTarget] = useState(null);
  const [scannedPack, setScannedPack] = useState(null);
  const [search, setSearch] = useState('');
  const visibleProducts = search.trim() ? products : products.slice(0, frequentLimit);
  const [loading, setLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const customerNameRef = useRef('');
  customerNameRef.current = customerName;
  const [discount, setDiscount] = useState(0);
  const [cashReceived, setCashReceived] = useState('');
  const [aiSearchOpen, setAiSearchOpen] = useState(false);
  const [clinicalBlocked, setClinicalBlocked] = useState(false);
  const [controlledGateOpen, setControlledGateOpen] = useState(false);
  const [pharmacistApprovalId, setPharmacistApprovalId] = useState(null);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [prescriptionNotes, setPrescriptionNotes] = useState('');
  const [saleAttachments, setSaleAttachments] = useState([]);
  const [prescriptionInfo, setPrescriptionInfo] = useState(null);
  const { isOffline, offlineReady, offlineStatus, cachingProducts, handleCacheProducts, cacheRevision } = useOfflinePreparation();
  const [saleCount, setSaleCount] = useState(0);
  const [customerCategory, setCustomerCategory] = useState('cash');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [corporateClients, setCorporateClients] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [selectedCorporate, setSelectedCorporate] = useState(null);
  const [policyNumber, setPolicyNumber] = useState('');
  const [coveragePercent, setCoveragePercent] = useState(80);
  const [employeeId, setEmployeeId] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);

  // ── Loyalty state ─────────────────────────────────────────
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [loyaltyAccount, setLoyaltyAccount] = useState(null);      // {loyalty_id, tier, balance, ...}
  const [loyaltySettings, setLoyaltySettings] = useState(null);     // program config
  const [loyaltyRedeemPoints, setLoyaltyRedeemPoints] = useState(0);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [loyaltyLookupBusy, setLoyaltyLookupBusy] = useState(false);
  const [loyaltyManualCode, setLoyaltyManualCode] = useState('');

  // Handler for the manual "scan" input inside the checkout dialog.
  const handleLoyaltyManualScan = async () => {
    const code = (loyaltyManualCode || '').trim().toUpperCase();
    if (!code) return;
    try {
      const res = await api.post('/loyalty/scan', { code });
      const acc = res.data?.account;
      if (!acc) throw new Error('missing account');
      setLoyaltyEnabled(!!res.data.enabled);
      setLoyaltyAccount(acc);
      setLoyaltySettings(res.data.settings || null);
      if (acc.name && !customerName) setCustomerName(acc.name);
      if (acc.phone && !customerPhone) setCustomerPhone(acc.phone);
      toast.success(`${acc.name || 'Member'} · ${acc.tier} · ${(acc.balance || 0).toLocaleString()} pts`);
      setLoyaltyManualCode('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Loyalty card not recognised');
    }
  };

  // Email receipt state
  const [emailReceipt, setEmailReceipt] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [emailingReceipt, setEmailingReceipt] = useState(false);

  // SMS receipt state (Arkesel Ghana)
  const [smsReceipt, setSmsReceipt] = useState(false);
  const [smsingReceipt, setSmsingReceipt] = useState(false);
  // Holds the pharmacy's saved SMS preference so the cashier checkbox can default ON when desired
  const [smsDefault, setSmsDefault] = useState(false);

  // Hardware state
  const [printerConnected, setPrinterConnected] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);

  // Expiry warning state
  const [expiryWarnings, setExpiryWarnings] = useState({}); // { product_id: { has_warning, expired_batches } }

  // Drug-interaction alerts at POS — checked when a patient is linked and cart changes
  const [interactionAlerts, setInteractionAlerts] = useState([]);
  const [allergyAlerts, setAllergyAlerts] = useState([]);
  const [foodAlerts, setFoodAlerts] = useState([]);
  const [interactionDismissed, setInteractionDismissed] = useState(false);
  const [foodAlertsDismissed, setFoodAlertsDismissed] = useState(false);
  useEffect(() => {
    // Reset dismissed state whenever the cart or patient changes
    setInteractionDismissed(false);
    setFoodAlertsDismissed(false);
  }, [selectedPatientId, cart.length]);
  // Drug-interaction / food / allergy checks at POS. Patient-dependent alerts
  // (allergy, current-medication interactions) only populate once a patient is
  // linked; solo food-counseling points (e.g. warfarin + vitamin K) and cart-only
  // drug–drug interactions can fire from the cart alone, so the check runs
  // whenever the cart has at least one item, with or without a linked patient.
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      if (cart.length === 0 || isOffline) {
        if (active) { setInteractionAlerts([]); setAllergyAlerts([]); setFoodAlerts([]); }
        return;
      }
      try {
        const cartNames = [...new Set(cart.map((i) => i.generic_name || i.product_name || '').filter(Boolean))];
        if (cartNames.length === 0) { if (active) { setInteractionAlerts([]); setAllergyAlerts([]); setFoodAlerts([]); } return; }
        const res = await api.post('/pos/drug-interaction-check', { patient_id: selectedPatientId || null, cart_names: cartNames });
        if (active) {
          setInteractionAlerts(res.data.alerts || []);
          setAllergyAlerts(res.data.allergy_alerts || []);
          setFoodAlerts(res.data.food_alerts || []);
        }
      } catch {
        if (active) { setInteractionAlerts([]); setAllergyAlerts([]); setFoodAlerts([]); }
      }
    }, 900);
    return () => { active = false; clearTimeout(timer); };
  }, [selectedPatientId, cart, isOffline]);

  // Refill-eligibility warnings — "prescription expired" / "no refills left"
  // for the matched customer, sourced from the offline-cached refill
  // schedule so this works even without connectivity.
  const [refillWarnings, setRefillWarnings] = useState({}); // { product_id: message }
  useEffect(() => {
    let active = true;
    (async () => {
      if (!customerPhone || cart.length === 0) {
        if (active) setRefillWarnings({});
        return;
      }
      try {
        const entries = await getCachedRefillEligibility(customerPhone.trim());
        if (!entries.length) { if (active) setRefillWarnings({}); return; }
        const today = new Date().toISOString().slice(0, 10);
        const warnings = {};
        for (const item of cart) {
          const match = entries.find((e) => (e.approved_items || []).some((ai) => ai.product_id === item.product_id));
          if (!match) continue;
          if (match.valid_until && match.valid_until < today) {
            warnings[item.product_id] = `Prescription expired ${match.valid_until}`;
          } else if ((match.refills_consumed || 0) >= (match.refill_allowance || 0)) {
            warnings[item.product_id] = 'No refills remaining on file';
          }
        }
        if (active) setRefillWarnings(warnings);
      } catch {
        if (active) setRefillWarnings({});
      }
    })();
    return () => { active = false; };
  }, [cart, customerPhone]);

  // ── Hold / Recall transactions ──
  // Stored in localStorage so they survive app restarts and stay per-PC.
  // Scoped per (tenant, user) so each cashier sees only their own held carts.
  // Each held sale is { id, label, ts, cart, customerName, customerPhone,
  //   discount, customerCategory, selectedInsurance, policyNumber, coveragePercent,
  //   employeeId, prescriptionNotes, prescriptionInfo, selectedPatientId }
  const HELD_KEY = `pharmxai_held_sales:${user?.tenant_id || 'default'}:${user?.user_id || 'anon'}`;
  const [heldSales, setHeldSales] = useState([]);
  const [heldOpen, setHeldOpen] = useState(false);
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holdLabel, setHoldLabel] = useState('');
  const [pendingRecallId, setPendingRecallId] = useState(null);

  // Load held carts whenever the active user/tenant changes
  useEffect(() => {
    try {
      setHeldSales(JSON.parse(localStorage.getItem(HELD_KEY) || '[]'));
    } catch { setHeldSales([]); }
  }, [HELD_KEY]);

  const persistHeld = (next) => {
    setHeldSales(next);
    try {
      localStorage.setItem(HELD_KEY, JSON.stringify(next));
    } catch {
      // Quota exceeded or storage unavailable — let the cashier know it did not persist
      toast.error('Could not save held cart locally', {
        description: 'Browser storage is full or disabled. Held cart will be lost on reload.',
      });
    }
  };

  // Derived totals (must be before useEffects that reference them)
  const subtotal = cart.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  const total = Math.max(0, subtotal - discount - loyaltyDiscount);

  // ── Loyalty auto-lookup when phone is entered (debounced 500ms) ──
  useEffect(() => {
    const phone = (customerPhone || '').trim();
    if (!phone || phone.length < 6) {
      setLoyaltyAccount(null);
      setLoyaltyEnabled(false);
      setLoyaltyRedeemPoints(0);
      setLoyaltyDiscount(0);
      return;
    }
    setLoyaltyLookupBusy(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.post('/loyalty/lookup', {
          phone, name: customerNameRef.current || undefined,
        });
        setLoyaltyEnabled(!!res.data.enabled);
        setLoyaltyAccount(res.data.account || null);
        setLoyaltySettings(res.data.settings || null);
      } catch {
        setLoyaltyAccount(null);
      } finally {
        setLoyaltyLookupBusy(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [customerPhone]);

  // Recompute discount if redeem points change.
  useEffect(() => {
    if (!loyaltyAccount || !loyaltySettings || loyaltyRedeemPoints <= 0) {
      setLoyaltyDiscount(0);
      return;
    }
    const ratio = parseFloat(loyaltySettings.redeem_ratio) || 100;
    const raw = loyaltyRedeemPoints / ratio;
    // Cap at remaining subtotal so the cart never hits negative.
    const remaining = Math.max(0, subtotal - discount);
    setLoyaltyDiscount(Math.min(raw, remaining));
  }, [loyaltyRedeemPoints, loyaltyAccount, loyaltySettings, subtotal, discount]);

  const redeemAllPoints = () => {
    if (!loyaltyAccount || !loyaltySettings) return;
    const bal = parseInt(loyaltyAccount.balance || 0, 10);
    const min = parseInt(loyaltySettings.min_redeem_points || 0, 10);
    if (bal < min) return;
    const ratio = parseFloat(loyaltySettings.redeem_ratio) || 100;
    const remaining = Math.max(0, subtotal - discount);
    const pointsNeeded = Math.ceil(remaining * ratio);
    // Request at least `min` (the backend rejects anything below that
    // threshold before it applies its own discount cap) but never more than
    // what's actually needed to cover the sale — the backend will still cap
    // the real deduction down to `pointsNeeded` once the discount is capped
    // at `remaining`, so the customer never loses points beyond what's used.
    setLoyaltyRedeemPoints(Math.min(bal, Math.max(min, pointsNeeded)));
  };

  // The actual points that will be deducted once the backend applies its own
  // discount cap — may be less than loyaltyRedeemPoints (the requested
  // amount) when the sale subtotal is smaller than what was requested/the
  // minimum redeemable threshold. Shown in the checkout summary so the
  // cashier/customer see the real deduction, not the raw request.
  const effectiveLoyaltyPoints = loyaltyDiscount > 0
    ? Math.round(loyaltyDiscount * (parseFloat(loyaltySettings?.redeem_ratio) || 100))
    : 0;

  // Barcode scanner listener
  useEffect(() => {
    const cleanup = startBarcodeListener((barcode) => {
      const upper = (barcode || '').trim().toUpperCase();
      if (upper.startsWith('OP-')) {
        handlePackCode(upper);
        return;
      }

      // ── Loyalty card scan: LOY-XXXXXXXX ──
      if (upper.startsWith('LOY-')) {
        toast.info(`Loyalty card: ${upper}`, { duration: 1500 });
        api.post('/loyalty/scan', { code: upper })
          .then((res) => {
            const acc = res.data?.account;
            if (!acc) throw new Error('no account');
            setLoyaltyEnabled(!!res.data.enabled);
            setLoyaltyAccount(acc);
            setLoyaltySettings(res.data.settings || null);
            // Prefill customer fields — cashier can still edit before checkout.
            if (acc.name) setCustomerName(acc.name);
            if (acc.phone) setCustomerPhone(acc.phone);
            // Open checkout so the cashier sees the loyalty widget immediately.
            if (cart.length > 0) openCheckout();
            toast.success(`${acc.name || 'Member'} · ${acc.tier} · ${(acc.balance || 0).toLocaleString()} pts`);
          })
          .catch((err) => {
            const detail = err?.response?.data?.detail || 'Loyalty card not recognised';
            toast.error(detail);
          });
        return;
      }

      toast.info(`Scanned: ${barcode}`, { duration: 1500 });
      // Search for product by barcode, SKU, or product_id
      const found = products.find(p =>
        p.barcode === barcode || p.sku === barcode || p.product_id === barcode || p.name?.toLowerCase() === barcode.toLowerCase()
      );
      if (found) {
        addToCart(found);
        toast.success(`Added: ${found.name}`);
      } else {
        // Try API search
        api.get('/products', { params: { search: barcode, limit: 1 } })
          .then(res => {
            const p = res.data.products?.[0];
            if (p) {
              addToCart(p);
              toast.success(`Added: ${p.name}`);
            } else {
              toast.error(`Product not found for barcode: ${barcode}`);
            }
          })
          .catch(() => toast.error(`Scan lookup failed: ${barcode}`));
      }
    });
    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, cart.length]);

  // Update customer display when cart changes
  useEffect(() => {
    if (isCustomerDisplayOpen()) {
      updateCustomerDisplay({
        items: cart,
        total,
        status: cart.length > 0 ? 'scanning' : 'ready',
      });
    }
  }, [cart, total]);

  // Hardware actions
  const handleTogglePrinter = async () => {
    if (printerConnected) {
      await disconnectThermalPrinter();
      setPrinterConnected(false);
      toast.info('Printer disconnected');
      return;
    }
    try {
      const ok = await connectThermalPrinter();
      if (ok) {
        setPrinterConnected(true);
        toast.success('Thermal printer connected');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to connect printer');
    }
  };

  const handleToggleDisplay = () => {
    if (isCustomerDisplayOpen()) {
      closeCustomerDisplay();
      setDisplayOpen(false);
      toast.info('Customer display closed');
    } else {
      try {
        openCustomerDisplay();
        setDisplayOpen(true);
        toast.success('Customer display opened');
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  // Load insurance providers and corporate clients
  useEffect(() => {
    const loadPartners = async () => {
      try {
        const [insRes, corpRes] = await Promise.all([
          api.get('/insurance-providers'),
          api.get('/corporate-clients'),
        ]);
        setInsuranceProviders(insRes.data.providers || []);
        setCorporateClients(corpRes.data.clients || []);
      } catch { /* offline or error - continue */ }
    };
    if (navigator.onLine) loadPartners();
  }, []);

  // Pull pharmacy-level SMS preference (so cashier checkbox can default ON)
  useEffect(() => {
    if (!navigator.onLine) return;
    api.get('/settings/sms')
      .then((res) => {
        if (res.data?.sms_enabled && res.data?.receipt_sms_default) {
          setSmsDefault(true);
          setSmsReceipt(true);
        }
      })
      .catch(() => { /* permission-gated or offline — ignore */ });
  }, []);

  // Check for prescription data from Rx Scanner
  useEffect(() => {
    const storedData = sessionStorage.getItem('prescription_pos_data');
    if (!storedData) return;
    try {
      const posData = JSON.parse(storedData);

      if (posData.patientName) setCustomerName(posData.patientName);
      if (posData.prescriptionInfo) setPrescriptionInfo(posData.prescriptionInfo);

      // Build notes from drug interactions
      let notes = '';
      if (posData.interactionNotes) {
        notes += `Drug Interaction Summary: ${posData.interactionNotes}\n`;
      }
      if (posData.interactionWarnings?.length > 0) {
        notes += `Warnings: ${posData.interactionWarnings.join('; ')}\n`;
      }
      if (notes) setPrescriptionNotes(notes.trim());

      if (posData.cartItems?.length > 0) {
        setCart(posData.cartItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          batch_number: item.batch_number || '',
          max_stock: item.max_stock || 999,
          fromPrescription: true,
        })));
        toast.success(`Loaded ${posData.cartItems.length} items from prescription`, {
          description: `Patient: ${posData.patientName || 'Unknown'}`,
        });
      }

      sessionStorage.removeItem('prescription_pos_data');
    } catch (err) {
      console.error('Failed to parse prescription data:', err);
    }
  }, []);

  const handleBarcodeScan = async (barcode) => {
    if (!barcode.trim()) return;
    if (barcode.trim().toUpperCase().startsWith('OP-')) { await handlePackCode(barcode.trim().toUpperCase()); return; }
    try {
      if (isOffline && offlineReady) {
        // Search cached products by barcode
        const cachedProducts = await searchCachedProducts(barcode);
        const found = cachedProducts.find(p => p.barcode === barcode.trim());
        if (found) {
          addToCart(found);
          toast.success(`Added: ${found.name}`);
        } else {
          toast.error('No product found with this barcode (offline)');
        }
      } else {
        const res = await api.get(`/products/barcode/${barcode.trim()}`);
        addToCart(res.data);
        toast.success(`Added: ${res.data.name}`);
      }
    } catch {
      toast.error('No product found with this barcode');
    }
  };

  // Patient autocomplete for checkout — when offline, falls back to the
  // locally cached recent-customers list so a repeat customer can still be
  // looked up (no patient_id link for these, just name/phone prefill).
  const searchPatients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setPatientSuggestions([]);
      return;
    }
    if (isOffline) {
      try {
        const cached = await searchCachedCustomers(query);
        setPatientSuggestions(cached.map((c) => ({
          patient_id: null, name: c.name, phone: c.phone, is_cached: true,
        })));
        setShowPatientSuggestions(true);
      } catch {
        setPatientSuggestions([]);
      }
      return;
    }
    try {
      const res = await api.get('/patients/autocomplete', { params: { q: query } });
      setPatientSuggestions(res.data.results || []);
      setShowPatientSuggestions(true);
    } catch {
      setPatientSuggestions([]);
    }
  }, [isOffline]);

  const handleCustomerNameChange = (value) => {
    setCustomerName(value);
    setSelectedPatientId(null); // Clear linked patient when typing
    searchPatients(value);
  };

  const selectPatient = (patient) => {
    setCustomerName(patient.name);
    setCustomerPhone(patient.phone || '');
    setSelectedPatientId(patient.is_cached ? null : patient.patient_id);
    if (patient.email) {
      setCustomerEmail(patient.email);
      setEmailReceipt(true);
    }
    setShowPatientSuggestions(false);
    setPatientSuggestions([]);
  };

  const fetchProducts = useCallback(async () => {
    const request = ++catalogRequest.current;
    setLoading(true);
    try {
      const data = isOffline && offlineReady
        ? await loadCachedCatalog(search, catalogPage)
        : await loadOnlineCatalog(search, catalogPage);
      if (request !== catalogRequest.current) return;
      setProducts(data.products || []);
      setCatalogTotal(data.total || 0);
    } catch (err) {
      console.error('Products fetch error:', err);
      // Fallback to cached products
      if (offlineReady) {
        try {
          const data = await loadCachedCatalog(search, catalogPage);
          if (request !== catalogRequest.current) return;
          setProducts(data.products);
          setCatalogTotal(data.total);
        } catch (e) {
          console.error('Cache fetch failed:', e);
        }
      }
    } finally {
      if (request === catalogRequest.current) setLoading(false);
    }
  }, [search, catalogPage, isOffline, offlineReady]);

  const changeSearch = (value) => { setCatalogPage(1); setSearch(value); };

  useEffect(() => {
    const timer = setTimeout(fetchProducts, 300);
    return () => clearTimeout(timer);
  }, [fetchProducts, cacheRevision]);

  const handlePackCode = async (code) => {
    window.dispatchEvent(new CustomEvent('pos-pack-scanned', { detail: code }));
    try {
      const { data } = await api.get('/scan-pos/lookup', { params: { code } });
      const p = data.choices?.find(x => x.pack?.pack_id === code);
      if (!p) throw new Error('Pack is expired, recalled or unavailable');
      handlePhonePack(p.pack, p);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const handlePhonePack = (pack, product) => {
    if (!scannerCartRef.current.some(i => i.product_id === product.product_id)) { toast.info('Select the medicine and quantity before confirming its pack sources.'); return; }
    setScannedPack(pack); setPackTarget(product.product_id);
  };
  const handlePhoneMedicine = (product, quantity) => {
    setCart(prev => {
      const old = prev.find(i => i.product_id === product.product_id);
      if (old) return prev.map(i => i.product_id === product.product_id ? { ...i, quantity: i.quantity+quantity, pack_sources: undefined } : i);
      return [...prev, { product_id: product.product_id, product_name: product.name, quantity, unit_price: product.unit_price,
        max_stock: product.stock_quantity, is_controlled: !!product.is_controlled, pack_tracked: !!product.loose_unit, batch_number: '' }];
    });
  };
  const addToCart = (product) => {
    if (product.stock_quantity <= 0) {
      toast.error('Product is out of stock');
      return;
    }

    // Check for expired batch warnings (async, non-blocking for cart add)
    const liveBatches = (product.batches || []).filter((b) => (b.quantity || 0) > 0);
    if (product.recall_locked || (liveBatches.length > 0 && liveBatches.every((b) => b.recalled))) {
      toast.error(`RECALLED: ${product.name} cannot be sold`, { description: 'This stock is under a platform recall. Set it aside.', duration: 8000 });
      return;
    }
    if (!isPOSAvailable(product)) {
      toast.error('No unexpired stock is available for this product');
      return;
    }
    const availableBatches = (product.batches || []).filter(batch => isPOSBatchAvailable(batch))
      .sort((a, b) => (Date.parse(a.expiry_date) || Infinity) - (Date.parse(b.expiry_date) || Infinity));
    const availableStock = availableBatches.length
      ? Math.min(product.stock_quantity, availableBatches.reduce((sum, batch) => sum + Number(batch.quantity), 0))
      : product.stock_quantity;
    if (!expiryWarnings[product.product_id] && !isOffline) {
      api.get(`/expired-products/pos-check/${product.product_id}`).then(res => {
        if (res.data.has_warning) {
          setExpiryWarnings(prev => ({ ...prev, [product.product_id]: res.data }));
          toast.warning(
            `WARNING: ${product.name} has ${res.data.expired_batches.length} expired batch(es) NOT removed from shelf!`,
            { duration: 8000, description: 'Verify you are dispensing a non-expired batch before selling.' }
          );
        }
      }).catch(() => {});
    }

    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.product_id);
      if (existing) {
        if (existing.quantity >= availableStock) {
          toast.error('Not enough stock');
          return prev;
        }
        return prev.map(item =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.product_id,
        product_name: product.name,
        generic_name: product.generic_name || '',
        quantity: 1,
        unit_price: product.unit_price,
        batch_number: availableBatches[0]?.batch_number || '',
        max_stock: availableStock,
        is_controlled: !!product.is_controlled,
        pack_tracked: !!product.loose_unit,
      }];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id !== productId) return item;
      const newQty = item.quantity + delta;
      if (newQty <= 0) return null;
      if (newQty > item.max_stock) {
        toast.error('Not enough stock');
        return item;
      }
      return { ...item, quantity: newQty };
    }).filter(Boolean));
  };

  // Typed quantity — allows a transient empty field, clamps to available stock.
  const handleQuantityInput = (productId, raw) => {
    if (raw === '') {
      setCart(prev => prev.map(ci => ci.product_id === productId ? { ...ci, quantity: '' } : ci));
      return;
    }
    const num = parseInt(raw, 10);
    if (isNaN(num) || num < 1) return;
    const item = cart.find(ci => ci.product_id === productId);
    if (item && num > item.max_stock) {
      toast.error(`Max stock: ${item.max_stock}`);
      return;
    }
    setCart(prev => prev.map(ci => ci.product_id === productId ? { ...ci, quantity: num } : ci));
  };

  const handleQuantityBlur = (productId, raw) => {
    const val = parseInt(raw, 10);
    if (!val || val < 1) {
      setCart(prev => prev.map(ci => ci.product_id === productId ? { ...ci, quantity: 1 } : ci));
    }
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
    setPharmacistApprovalId(null); // Clear approval when cart changes
  };

  const swapCartItem = (productId, target) => {
    setCart(prev => prev.map(ci => ci.product_id === productId ? {
      ...ci,
      product_id: target.product_id,
      product_name: target.name,
      unit_price: target.unit_price,
      max_stock: target.valid_stock,
      quantity: Math.min(ci.quantity, target.valid_stock),
    } : ci));
  };

  /** Check whether the cart needs controlled-substance approval before checkout opens. */
  const openCheckout = useCallback(() => {
    if (cart.length === 0) return;
    const hasControlled = cart.some((item) => item.is_controlled);
    if (hasControlled && !pharmacistApprovalId) {
      setControlledGateOpen(true);
      return;
    }
    setCheckoutOpen(true);
  }, [cart, pharmacistApprovalId]);

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    if (scanner.blocked) { toast.error('Finish scanning and resolve all captures, or explicitly end the scanner session.'); return; }
    if (cart.some(i => i.pack_tracked && (!i.pack_sources || i.pack_sources.reduce((n,p) => n+p.quantity,0) !== i.quantity))) { toast.error('Confirm current pack sources for each tracked medicine.'); return; }
    if (clinicalBlocked) { toast.error('Pharmacist review required before completing this sale.'); return; }
    if (paymentMethod === 'cash' && cashReceived !== '' && (!Number.isFinite(Number(cashReceived)) || Number(cashReceived) < total)) {
      toast.error('Cash received is less than the sale total');
      return;
    }
    setProcessing(true);

    // Update customer display with payment status
    if (isCustomerDisplayOpen()) {
      updateCustomerDisplay({ items: cart, total, status: 'payment', message: `Payment: ${paymentMethod.toUpperCase()}` });
    }

    const copayAmount = customerCategory === 'insurance' && selectedInsurance
      ? Math.round((total * (100 - coveragePercent)) / 100 * 100) / 100
      : undefined;
    const claimAmount = customerCategory === 'insurance' && selectedInsurance
      ? Math.round((total * coveragePercent) / 100 * 100) / 100
      : undefined;

    const saleData = {
      client_sale_id: clientSaleId,
      scanner_session_id: scanner.session?.session_id,
      items: cart.map(({ product_id, product_name, quantity, unit_price, batch_number, pack_sources, pack_override_reason }) => ({
        product_id, product_name, quantity, unit_price, batch_number, pack_sources, pack_override_reason,
      })),
      subtotal,
      payment_method: paymentMethod,
      customer_name: customerName,
      customer_phone: customerPhone,
      customer_email: emailReceipt && customerEmail ? customerEmail.trim() : undefined,
      customer_category: customerCategory,
      patient_id: selectedPatientId || undefined,
      discount,
      total,
      notes: prescriptionNotes || undefined,
      prescription_info: prescriptionInfo || undefined,
      insurance_provider_id: customerCategory === 'insurance' ? selectedInsurance?.provider_id : undefined,
      insurance_provider_name: customerCategory === 'insurance' ? selectedInsurance?.name : undefined,
      policy_number: customerCategory === 'insurance' ? policyNumber : undefined,
      coverage_percent: customerCategory === 'insurance' ? coveragePercent : undefined,
      copay_amount: copayAmount,
      claim_amount: claimAmount,
      corporate_client_id: customerCategory === 'corporate' ? selectedCorporate?.client_id : undefined,
      corporate_client_name: customerCategory === 'corporate' ? selectedCorporate?.name : undefined,
      corporate_employee_id: customerCategory === 'corporate' ? employeeId : undefined,
      cash_received: paymentMethod === 'cash' && cashReceived ? parseFloat(cashReceived) : undefined,
      change_due: paymentMethod === 'cash' && cashReceived && parseFloat(cashReceived) >= total
        ? Math.round((parseFloat(cashReceived) - total) * 100) / 100
        : undefined,
      loyalty_redeem_points: loyaltyRedeemPoints > 0 ? loyaltyRedeemPoints : undefined,
      pharmacist_approval_id: pharmacistApprovalId || undefined,
      attachments: saleAttachments.length ? saleAttachments.map((f) => f.file_id) : undefined,
    };

    try {
      if (isOffline || !navigator.onLine) {
        if (scanner.session || cart.some(i => i.pack_tracked || i.pack_sources?.length)) {
          toast.error('Phone-assisted and pack-tracked dispensing require a live stock check. Reconnect before completing.'); return;
        }
        // Save to IndexedDB for offline sync
        if (offlineReady) {
          for (const item of saleData.items) {
            const cached = await getCachedProduct(item.product_id);
            if (cached?.loose_unit) { toast.error('Pack-tracked medicine requires online source confirmation.'); return; }
            const batch = (cached?.batches || []).find((b) => b.batch_number === item.batch_number);
            if (cached?.recall_locked || batch?.recalled) {
              toast.error(`RECALLED: ${item.product_name} cannot be sold`, { description: 'Remove it from the cart. This stock is under a platform recall.', duration: 8000 });
              return;
            }
          }
          const offlineSale = await addOfflineSale(saleData);
          setLastSale({
            ...saleData,
            sale_id: offlineSale.offline_id,
            created_at: offlineSale.created_at,
            is_offline: true,
          });
          setCart([]);
          setCheckoutOpen(false);
          setReceiptOpen(true);
          resetCheckout();
          setSaleCount((c) => c + 1);
          toast.success('Sale saved offline!', {
            description: 'Will sync automatically when online',
          });
          fetchProducts();
        } else {
          toast.error('Offline mode not ready. Please cache products first when online.');
        }
        return;
      }

      const res = await api.post('/sales', saleData);
      setLastSale(res.data);
      setCart([]);
      setCheckoutOpen(false);
      setReceiptOpen(true);
      toast.success('Sale completed!');
      setSaleCount((c) => c + 1);
      fetchProducts();

      // Email receipt to customer (best-effort, after the sale dialog opens).
      // We capture the email + flag BEFORE resetCheckout wipes them.
      const wantEmail = emailReceipt;
      const emailTarget = (customerEmail || '').trim();
      const wantSms = smsReceipt;
      const smsTarget = (customerPhone || '').trim();
      resetCheckout();
      if (wantEmail && emailTarget) {
        emailReceiptForSale(res.data, emailTarget, { silent: false });
      }
      if (wantSms && smsTarget) {
        smsReceiptForSale(res.data, smsTarget, { silent: false });
      }

      // Hardware actions after successful sale
      // 1. Open cash drawer for cash payments
      if (paymentMethod === 'cash') {
        openCashDrawerSafe().then(opened => {
          if (opened) toast.info('Cash drawer opened', { duration: 1500 });
        });
      }
      // 2. Auto-print receipt if thermal printer connected
      if (isThermalPrinterConnected()) {
        smartPrintReceipt(res.data).catch(() => {});
      }
      // 3. Update customer display
      if (isCustomerDisplayOpen()) {
        updateCustomerDisplay({ items: [], total: res.data.total, status: 'complete', message: 'Thank you!' });
        setTimeout(() => updateCustomerDisplay({ items: [], total: 0, status: 'ready' }), 5000);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Sale response unavailable. Reconnect and retry this same sale; it has not been copied into the offline queue.');
    } finally {
      setProcessing(false);
    }
  };

  const resetCheckout = () => {
    setClientSaleId(crypto.randomUUID());
    setPackTarget(null); setScannedPack(null);
    setPaymentMethod('cash');
    setCustomerName('');
    setCustomerPhone('');
    setDiscount(0);
    setCashReceived('');
    setPrescriptionNotes('');
    setSaleAttachments([]);
    setPrescriptionInfo(null);
    setCustomerCategory('cash');
    setSelectedInsurance(null);
    setSelectedCorporate(null);
    setPolicyNumber('');
    setCoveragePercent(80);
    setEmployeeId('');
    setSelectedPatientId(null);
    setPatientSuggestions([]);
    setShowPatientSuggestions(false);
    setEmailReceipt(false);
    setCustomerEmail('');
    // SMS — preserve the pharmacy default but clear per-sale flag
    setSmsReceipt(smsDefault);
    // Loyalty
    setLoyaltyAccount(null);
    setLoyaltyEnabled(false);
    setLoyaltyRedeemPoints(0);
    setLoyaltyDiscount(0);
    setLoyaltyManualCode('');
  };

  // ── Email receipt to customer ──
  // Best-effort: requires SMTP configured server-side. Reuses sale_id created
  // during checkout. Returns silently if no email provided.
  const emailReceiptForSale = async (sale, toEmail, { silent = false } = {}) => {
    const target = (toEmail || '').trim();
    if (!sale?.sale_id) return { ok: false };
    if (!target) {
      if (!silent) toast.error('Provide a customer email first');
      return { ok: false };
    }
    if (sale.is_offline) {
      // We do not have a server-side sale yet; queue intent on the sale itself
      if (!silent) {
        toast.info('Receipt will be emailed once the sale syncs online', {
          description: target,
        });
      }
      return { ok: false, queued: true };
    }
    setEmailingReceipt(true);
    try {
      await api.post(`/sales/${sale.sale_id}/email-receipt`, { to_email: target });
      if (!silent) toast.success(`Receipt emailed to ${target}`);
      return { ok: true };
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Email send failed';
      if (!silent) toast.error('Email failed', { description: detail });
      return { ok: false, error: detail };
    } finally {
      setEmailingReceipt(false);
    }
  };

  // ── SMS receipt to customer (Arkesel) ──
  const smsReceiptForSale = async (sale, toPhone, { silent = false } = {}) => {
    const target = (toPhone || '').trim();
    if (!sale?.sale_id) return { ok: false };
    if (!target) {
      if (!silent) toast.error('Provide a phone number first');
      return { ok: false };
    }
    if (sale.is_offline) {
      if (!silent) {
        toast.info('SMS will be sent once the sale syncs online', { description: target });
      }
      return { ok: false, queued: true };
    }
    setSmsingReceipt(true);
    try {
      const { data } = await api.post(`/sales/${sale.sale_id}/sms-receipt`, { to: target });
      if (!silent) toast.success(`SMS sent to ${data.to || target}`);
      return { ok: true, ...data };
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'SMS send failed';
      if (!silent) toast.error('SMS failed', { description: detail });
      return { ok: false, error: detail };
    } finally {
      setSmsingReceipt(false);
    }
  };

  // ── Hold / Recall handlers ──
  const clearActiveCart = () => {
    setCart([]);
    resetCheckout();
  };

  const heldSnapshot = (id, label) => ({
    id,
    label,
    ts: new Date().toISOString(),
    cart,
    customerName,
    customerPhone,
    discount,
    customerCategory,
    selectedInsurance,
    selectedCorporate,
    policyNumber,
    coveragePercent,
    employeeId,
    prescriptionNotes,
    prescriptionInfo,
    selectedPatientId,
  });

  const applyHeldEntry = (entry) => {
    setClientSaleId(crypto.randomUUID());
    setCart(entry.cart || []);
    setCustomerName(entry.customerName || '');
    setCustomerPhone(entry.customerPhone || '');
    setDiscount(entry.discount || 0);
    setCustomerCategory(entry.customerCategory || 'cash');
    setSelectedInsurance(entry.selectedInsurance || null);
    setSelectedCorporate(entry.selectedCorporate || null);
    setPolicyNumber(entry.policyNumber || '');
    setCoveragePercent(entry.coveragePercent || 80);
    setEmployeeId(entry.employeeId || '');
    setPrescriptionNotes(entry.prescriptionNotes || '');
    setPrescriptionInfo(entry.prescriptionInfo || null);
    setSelectedPatientId(entry.selectedPatientId || null);
  };

  const openHoldDialog = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    // Default label: customer name or item count
    const defaultLabel = customerName?.trim() || `${cart.length} item${cart.length > 1 ? 's' : ''}`;
    setHoldLabel(defaultLabel);
    setHoldDialogOpen(true);
  };

  const confirmHoldCart = () => {
    if (cart.length === 0) return;
    const id = `held_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const label = holdLabel?.trim() || `${cart.length} item${cart.length > 1 ? 's' : ''}`;
    const entry = heldSnapshot(id, label);
    persistHeld([entry, ...heldSales]);
    clearActiveCart();
    setHoldDialogOpen(false);
    setHoldLabel('');
    toast.success(`Sale held: ${entry.label}`, {
      description: 'Recall it any time from the Recall menu.',
    });
  };

  const doRecallCart = (id) => {
    const entry = heldSales.find(h => h.id === id);
    if (!entry) return;
    applyHeldEntry(entry);
    persistHeld(heldSales.filter(h => h.id !== id));
    setHeldOpen(false);
    setPendingRecallId(null);
    toast.success(`Recalled: ${entry.label}`);
  };

  const recallCart = (id) => {
    if (cart.length > 0) {
      // Ask the user to confirm they want to overwrite the current cart
      setPendingRecallId(id);
      return;
    }
    doRecallCart(id);
  };

  const deleteHeldCart = (id) => {
    const entry = heldSales.find(h => h.id === id);
    persistHeld(heldSales.filter(h => h.id !== id));
    toast.info(`Removed: ${entry?.label || 'held sale'}`);
  };

  // Auto-hold current cart then recall the pending one (used by POSRecallConfirmDialog)
  const handleHoldAndRecall = (idToRecall) => {
    const id = `held_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const label = customerName?.trim() || `${cart.length} item${cart.length > 1 ? 's' : ''}`;
    const entry = heldSnapshot(id, label);
    const nextHeld = [entry, ...heldSales];
    persistHeld(nextHeld);
    clearActiveCart();
    setPendingRecallId(null);
    const target = nextHeld.find(h => h.id === idToRecall);
    if (target) {
      applyHeldEntry(target);
      persistHeld(nextHeld.filter(h => h.id !== idToRecall));
      setHeldOpen(false);
      toast.success(`Held current as "${label}", recalled "${target.label}"`);
    }
  };

  const cartHandlers = {
    onUpdateQuantity: updateQuantity,
    onQuantityInput: handleQuantityInput,
    onQuantityBlur: handleQuantityBlur,
    onRemove: removeFromCart,
  };

  return (
    <div className="pos-workspace flex flex-col min-h-0" data-testid="pos-page">
      <OpeningFloatDialog />
      <POSOfflineBanners
        isOffline={isOffline}
        offlineReady={offlineReady}
        offlineStatus={offlineStatus}
        cachingProducts={cachingProducts}
        onCacheProducts={handleCacheProducts}
      />
      <TillRecallBanner offlineReady={offlineReady} refreshKey={saleCount} />
      <TillScannerPanel scanner={scanner} onMedicine={handlePhoneMedicine} onPack={handlePhonePack} />
      {!!cart.length && <div className="scan-pack-controls flex flex-wrap gap-2 mb-2" data-testid="pos-pack-controls">
        {cart.map(item => <Button key={item.product_id} size="sm" variant="outline" data-testid={`pos-pack-sources-${item.product_id}`} onClick={() => { setScannedPack(null); setPackTarget(item.product_id); }}>Pack sources · {item.product_name}{item.pack_sources?.length ? ' ✓' : ''}</Button>)}
      </div>}
      {packTarget && cart.some(i => i.product_id === packTarget) && <OpenedPackDialog item={cart.find(i => i.product_id === packTarget)} scannedPack={scannedPack} onClose={() => setPackTarget(null)} onConfirm={(sources, reason) => setCart(prev => prev.map(i => i.product_id === packTarget ? { ...i, pack_tracked: true, pack_sources: sources, pack_override_reason: reason } : i))} />}
      {!!lastSale?.items?.some(i => i.pack_sources?.some(p => p.new_pack)) && <div className="flex flex-wrap gap-2 mb-2" data-testid="pos-new-pack-labels">
        {lastSale.items.flatMap(i => i.pack_sources || []).filter(p => p.new_pack).map(p => <Button size="sm" variant="outline" key={p.pack_id} data-testid={`pos-print-new-${p.pack_id}`} onClick={() => printPackLabel(p.pack_id)}>Print label · {p.pack_id}</Button>)}
      </div>}
      <POSHardwareBar
        printerConnected={printerConnected}
        displayOpen={displayOpen}
        heldCount={heldSales.length}
        onTogglePrinter={handleTogglePrinter}
        onToggleDisplay={handleToggleDisplay}
        onOpenDrawer={() => openCashDrawerSafe().then(ok => ok ? toast.success('Drawer opened') : toast.error('Connect printer first'))}
        onOpenRxScanner={() => navigate('/prescriptions')}
        onOpenHeldSales={() => setHeldOpen(true)}
      />

      {/* Desktop Layout */}
      <div className="pos-desktop-layout hidden md:grid gap-4 flex-1 min-h-0">
        <div className="flex flex-col gap-3 min-w-0 min-h-0" data-testid="product-catalog">
          <POSCatalogToolbar
            search={search}
            onSearchChange={changeSearch}
            isOffline={isOffline}
            offlineReady={offlineReady}
            cachingProducts={cachingProducts}
            onCacheProducts={handleCacheProducts}
            onOpenAiSearch={() => setAiSearchOpen(true)}
            onBarcodeScan={handleBarcodeScan}
          />
          <POSCatalogHeading search={search} total={search.trim() ? catalogTotal : visibleProducts.length} displayLimit={frequentLimit} page={catalogPage} onPageChange={setCatalogPage} />
          <div className="pos-catalog-viewport flex-1 min-h-0 overflow-y-auto overscroll-contain" tabIndex={0} role="region" aria-label="Product catalogue" data-testid="pos-catalog-viewport">
            <POSProductGrid products={visibleProducts} loading={loading} onAddToCart={addToCart} searching={!!search.trim()} />
          </div>
        </div>

        <div className="flex flex-col min-w-0 min-h-0" data-testid="cart-sidebar">
          <POSCartPanel
            cart={cart}
            subtotal={subtotal}
            total={total}
            prescriptionInfo={prescriptionInfo}
            prescriptionNotes={prescriptionNotes}
            expiryWarnings={expiryWarnings}
            refillWarnings={refillWarnings}
            selectedPatientId={selectedPatientId}
            interactionAlerts={interactionAlerts}
            allergyAlerts={allergyAlerts}
            foodAlerts={foodAlerts}
            interactionDismissed={interactionDismissed}
            foodAlertsDismissed={foodAlertsDismissed}
            onDismissInteractions={() => setInteractionDismissed(true)}
            onDismissFoodAlerts={() => setFoodAlertsDismissed(true)}
            {...cartHandlers}
            onSwap={swapCartItem}
            onHold={openHoldDialog}
            onCheckout={() => openCheckout()}
          />
        </div>
      </div>

      <POSMobileView
        search={search}
        onSearchChange={changeSearch}
        catalogTotal={search.trim() ? catalogTotal : visibleProducts.length}
        catalogPage={catalogPage}
        onCatalogPageChange={setCatalogPage}
        products={visibleProducts}
        loading={loading}
        onAddToCart={addToCart}
        cart={cart}
        total={total}
        cartOpen={showMobileCart}
        onCartOpenChange={setShowMobileCart}
        interactionAlerts={interactionAlerts}
        interactionDismissed={interactionDismissed}
        onDismissInteractions={() => setInteractionDismissed(true)}
        {...cartHandlers}
        onHold={() => { setShowMobileCart(false); openHoldDialog(); }}
        onCheckout={() => { setShowMobileCart(false); openCheckout(); }}
      />

      <POSAISearchDialog
        open={aiSearchOpen}
        onOpenChange={setAiSearchOpen}
        initialQuery={search}
        onAddToCart={addToCart}
      />

      <POSCheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        cart={cart}
        subtotal={subtotal}
        total={total}
        customer={{
          category: customerCategory,
          setCategory: setCustomerCategory,
          name: customerName,
          onNameChange: handleCustomerNameChange,
          phone: customerPhone,
          setPhone: setCustomerPhone,
          suggestions: patientSuggestions,
          showSuggestions: showPatientSuggestions,
          setShowSuggestions: setShowPatientSuggestions,
          onSelectPatient: selectPatient,
          selectedPatientId,
        }}
        payment={{
          method: paymentMethod,
          setMethod: setPaymentMethod,
          discount,
          setDiscount,
          cashReceived,
          setCashReceived,
        }}
        partners={{
          insuranceProviders,
          corporateClients,
          selectedInsurance,
          setSelectedInsurance,
          selectedCorporate,
          setSelectedCorporate,
          policyNumber,
          setPolicyNumber,
          coveragePercent,
          setCoveragePercent,
          employeeId,
          setEmployeeId,
        }}
        loyalty={{
          enabled: loyaltyEnabled,
          account: loyaltyAccount,
          settings: loyaltySettings,
          redeemPoints: loyaltyRedeemPoints,
          setRedeemPoints: setLoyaltyRedeemPoints,
          effectivePoints: effectiveLoyaltyPoints,
          discount: loyaltyDiscount,
          lookupBusy: loyaltyLookupBusy,
          manualCode: loyaltyManualCode,
          setManualCode: setLoyaltyManualCode,
          onManualScan: handleLoyaltyManualScan,
          onRedeemAll: redeemAllPoints,
        }}
        receipts={{
          emailReceipt,
          setEmailReceipt,
          customerEmail,
          setCustomerEmail,
          smsReceipt,
          setSmsReceipt,
        }}
        prescription={{
          info: prescriptionInfo,
          notes: prescriptionNotes,
          setNotes: setPrescriptionNotes,
          attachments: saleAttachments,
          setAttachments: setSaleAttachments,
        }}
        clinicalBlocked={clinicalBlocked}
        onClinicalBlockedChange={setClinicalBlocked}
        processing={processing}
        onConfirm={handleCheckout}
        interactionAlerts={interactionAlerts}
        allergyAlerts={allergyAlerts}
        foodAlerts={foodAlerts}
        selectedPatientId={selectedPatientId}
      />

      <POSControlledSubstanceGate
        cart={cart}
        open={controlledGateOpen}
        onOpenChange={setControlledGateOpen}
        selectedPatientId={selectedPatientId}
        onApproved={(id, patient) => { setPharmacistApprovalId(id); if (patient) selectPatient(patient); setCheckoutOpen(true); }}
      />


      <POSReceiptDialog
        open={receiptOpen}
        onOpenChange={setReceiptOpen}
        sale={lastSale}
        printerConnected={printerConnected}
        emailingReceipt={emailingReceipt}
        customerEmail={customerEmail}
        onEmailReceipt={emailReceiptForSale}
        smsingReceipt={smsingReceipt}
        customerPhone={customerPhone}
        onSmsReceipt={smsReceiptForSale}
      />
      <POSHoldSaleDialog
        open={holdDialogOpen}
        onOpenChange={setHoldDialogOpen}
        holdLabel={holdLabel}
        setHoldLabel={setHoldLabel}
        onConfirm={confirmHoldCart}
        cartLength={cart.length}
        total={total}
        customerName={customerName}
      />
      <POSHeldSalesDialog
        open={heldOpen}
        onOpenChange={setHeldOpen}
        heldSales={heldSales}
        onRecall={recallCart}
        onDelete={deleteHeldCart}
      />
      <POSRecallConfirmDialog
        pendingRecallId={pendingRecallId}
        onCancel={() => setPendingRecallId(null)}
        onHoldAndRecall={handleHoldAndRecall}
        onReplace={doRecallCart}
      />
    </div>
  );
}

export default function POSPage() {
  const { user } = useAuth();
  const scope = offlineScopeKey(user);
  // All till state (cart, patient, approvals, cached lists) belongs to one branch.
  return <POSWorkspace key={scope} />;
}
